import { usePostV4ManufacturerByRouteManufacturerIdProductAndRandmarSku3DModelMutation } from "randmar-api-client";
import { Input, useToast } from "randmarcomps";
import React, { useCallback, useId } from 'react';
import { useManufacturer } from '../../contexts/WithManufacturer';

interface IProps {
  randmarSKU?: string;
  refetchModel?: () => Promise<void>;
  children: React.ReactNode;
  disabled?: boolean;
}

function ModelUploadInput({ randmarSKU, refetchModel, children, disabled }: IProps) {
  const { applicationId } = useManufacturer();
  const inputId = useId();
  const { toast } = useToast();
  const [uploadModel, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdProductAndRandmarSku3DModelMutation();

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      toast({ variant: "destructive", title: "Error", description: "No file selected." });
      return;
    }

    if (!randmarSKU) {
      toast({ variant: "destructive", title: "Error", description: "Missing SKU." });
      return;
    }

    if (!applicationId) {
      toast({ variant: "destructive", title: "Error", description: "Missing manufacturer context." });
      return;
    }

    try {
      await uploadModel({
        routeManufacturerId: applicationId,
        randmarSku: randmarSKU,
        body: { file: selectedFile },
      }).unwrap();

      if (refetchModel) {
        try {
          await refetchModel();
        } catch (refreshError) {
          console.warn("Failed to refresh 3D model after upload.", refreshError);
        }
      }

      toast({ variant: "success", title: "Success", description: "Model uploaded successfully." });
    } catch (error) {
      console.error("Model upload failed:", error);
      toast({ variant: "destructive", title: "Error", description: "Model upload failed." });
    }
    event.target.value = '';
  }, [applicationId, randmarSKU, refetchModel, toast, uploadModel]);

  if (disabled) return <>{children}</>;

  return (
    <>
      <Input
        type="file"
        accept=".glb,.obj"
        multiple={false}
        onChange={handleFileChange}
        className="hidden"
        id={inputId}
        disabled={isLoading}
      />
      <label className="contents" htmlFor={inputId}>
        {children}
      </label>
    </>
  );
}

export default ModelUploadInput;
